export type DataAuditViewTab = 'overview' | 'queued';

export type DataAuditWritebackType = 'nwb' | 'owb' | 'fwb';

export type DataAuditWritebackStatusApiType = 'nwb' | 'owb';

export type DataAuditStatusOption = {
  key: string;
  label: string;
};

export const DATA_AUDIT_NWB_SOURCE_VALUES = [
  'meetings',
  'messaging',
  'engage',
  'automation_workflow',
  'crm',
  'chatbot',
  'referral',
];

export const DATA_AUDIT_OWB_FWB_SOURCE_VALUES = [
  'JOB_MATCHING',
  'ENGAGE',
  'CHATBOT',
  'AUTOMATION_WORKFLOW',
  'MESSAGING',
  'MEETINGS',
  'REFERRAL',
  'CRM',
];

export type DataAuditEntityType = {
  id: string;
  senseEntityType: string;
  entityType: string;
  atsEntityType: string;
  createAllowed: boolean;
  updateAllowed: boolean;
  deleteAllowed: boolean;
};

export type DataAuditWritebackDashboardCardApi = {
  writebackType: string;
  totalWritebacks: number;
  successCount: number;
  failureCount: number;
  currentlyInQueue: number;
};

export type DataAuditDashboardCard = {
  totalWritebacks: number;
  successRate: number;
  failureCount: number;
  currentlyInQueue: number;
};

export type DataAuditAuditTimeInfo = {
  timeCreated: string;
  timeDue: string;
  lastProcessedTime: string;
};

export type DataAuditAuditRetryInfo = {
  count: number;
  max: number;
  enabled: boolean;
  wasManuallyRetried: boolean;
  isNotRecoverable: string | null;
};

export type DataAuditSourceMetaDataApi = {
  sense_meta_data?: {workflow_id?: string | number};
};

export type DataAuditSourceMetaDataInput =
  | DataAuditSourceMetaDataApi
  | string
  | null
  | undefined;

export type DataAuditAuditEntryApi = {
  auditId: string;
  writebackId: string;
  writebackType: string;
  atsId: string;
  entityType: string;
  source: string;
  status: string;
  noteType?: string;
  atsSystem?: string;
  sourceMetaData?: DataAuditSourceMetaDataInput;
  timeInfo: DataAuditAuditTimeInfo;
  error?: string;
  retry: DataAuditAuditRetryInfo;
};

export type DataAuditAuditTableApiResponse = {
  totalElements: number;
  responses: Array<DataAuditAuditEntryApi>;
};

export type DataAuditAuditTableParams = {
  writebackType: DataAuditWritebackType;
  status?: string;
  source?: string;
  entityType?: string;
  noteType?: string;
  from?: string;
  to?: string;
  search?: string;
  page: number;
  size: number;
  sort?: 'asc' | 'desc';
};

export type DataAuditAuditTableState = {
  totalElements: number;
  entries: Array<DataAuditAuditEntryApi>;
  params: DataAuditAuditTableParams | null;
};

export type DataAuditHttpAuditRequestApi = {
  method: string;
  url: string;
  headers: string;
  body: string;
};

export type DataAuditHttpAuditResponseApi = {
  statusCode: number;
  encoding: string;
  headers: string;
  body: string;
};

export type DataAuditHttpAuditApi = {
  httpAuditId: string;
  auditId: string;
  request: DataAuditHttpAuditRequestApi;
  requestCurl: string;
  response: DataAuditHttpAuditResponseApi;
  timeCreated: string;
};

export type DataAuditAuditDetailApi = DataAuditAuditEntryApi & {
  httpAudits: Array<DataAuditHttpAuditApi>;
};

export type DataAuditAuditDetailState = {
  detail: DataAuditAuditDetailApi | null;
  auditId: string | null;
  writebackType: DataAuditWritebackType | null;
};

export type DataAuditAuditByAtsIdNodeApi = {
  writebackId?: string | null;
  queueId?: string | null;
  audits: Array<DataAuditAuditDetailApi>;
};

export type DataAuditAuditByAtsIdApiResponse =
  Array<DataAuditAuditByAtsIdNodeApi>;

export type DataAuditAuditByAtsIdState = {
  nodes: Array<DataAuditAuditByAtsIdNodeApi>;
  atsId: string | null;
  writebackType: DataAuditWritebackType | null;
};

export type DataAuditQueuedTimeInfo = {
  timeCreated: string;
  timeDue: string;
};

export type DataAuditQueuedOwbEntryApi = {
  id: string;
  atsId: string;
  atsEntityType: string;
  source: string;
  status: string;
  action: string;
  timeInfo: DataAuditQueuedTimeInfo;
};

export type DataAuditQueuedNwbEntryApi = {
  id: string;
  atsId: string;
  source: string;
  status: string;
  type: string;
  resourceId: string;
  metadata: Record<string, unknown>;
  timeInfo: DataAuditQueuedTimeInfo;
};

export type DataAuditQueuedEntryApi =
  | DataAuditQueuedOwbEntryApi
  | DataAuditQueuedNwbEntryApi;

export type DataAuditQueuedTableParams = {
  writebackType: DataAuditWritebackType;
  source?: string;
  atsEntityType?: string;
  noteType?: string;
  search?: string;
  page: number;
  size: number;
};

export type DataAuditQueuedTableApiResponse = {
  totalElements: number;
  responses: Array<DataAuditQueuedEntryApi>;
};

export type DataAuditQueuedTableState = {
  totalElements: number;
  entries: Array<DataAuditQueuedEntryApi>;
  params: DataAuditQueuedTableParams | null;
};

export type DataAuditWritebackGraphSourceApi = {
  source: string | null;
  successCount: number;
  failureCount: number;
};

export type DataAuditWritebackGraphApi = {
  type: string;
  successCount: number;
  failureCount: number;
  sourceWiseCount: Array<DataAuditWritebackGraphSourceApi>;
};

export type DataAuditWritebackBreakdownGraphsState = Record<
  DataAuditWritebackType,
  DataAuditWritebackGraphApi | null
>;

export type DataAuditSupportedFieldListItem = {
  label: string;
  value: string;
};

export type DataAuditSupportedFieldApi = {
  senseEntityType: string | null;
  entityType: string | null;
  atsEntityType: string | null;
  objectId: string;
  id: string;
  name: string | null;
  displayName: string | null;
  description: string | null;
  fieldGroup: string;
  dataType: string;
  possibleValues: Array<DataAuditSupportedFieldListItem> | null;
  overriddenPossibleValues: Array<DataAuditSupportedFieldListItem> | null;
  readOnly: boolean;
  createOperation: string;
  updateOperation: string;
  parentFieldId: string | null;
  isList: boolean;
  allowAddingNewValues: boolean;
  maxFieldLength: number | null;
  relatedEntity: unknown | null;
  childFields: Array<unknown>;
  atsFieldGroup: string;
  atsDataType: string;
  atsFieldId: string | null;
  deleted: boolean;
  metaData: Record<string, string> | null;
};

export type DataAuditSupportedFieldRow = {
  id: string;
  senseEntityType: string;
  entityType: string;
  atsEntityType: string;
  atsFieldName: string;
  displayName: string;
  possibleValues: string;
  overriddenPossibleValues: string;
  ignorePossibleValues: boolean;
};

export type DataAuditSupportedFieldSortKey = keyof Pick<
  DataAuditSupportedFieldRow,
  | 'senseEntityType'
  | 'entityType'
  | 'atsEntityType'
  | 'atsFieldName'
  | 'displayName'
  | 'possibleValues'
  | 'overriddenPossibleValues'
  | 'ignorePossibleValues'
>;
